import { useMemo } from 'react';
import { DatePicker } from '@mantine/dates';
import { Checkbox, Divider } from '@mantine/core';
import TextField from '../../components/TextField/TextField';
import { Dropdown } from '../../components/Dropdown/Dropdown';
import { ICreateAppointmentBuilder } from '../../hooks/useCreateAppointmentRequest';
import { IGeneralPractice } from '../../hooks/promises/getGeneralPractices';
import { ILookupTypes } from '../../hooks/common/useGetLookupTypes';
import { consultationTypeRequiresFollowUp } from './helpers';

interface IRegisterAppointmentFormHeaderProps {
  builder: ICreateAppointmentBuilder;
  setUserClosedModal: (value: React.SetStateAction<boolean>) => void;
  favouritePractices: IGeneralPractice[];
  lookupTypes: ILookupTypes;
  dailyCapacityCount: number | undefined;
}

function RegisterAppointmentFormHeader({
  builder,
  setUserClosedModal,
  favouritePractices,
  lookupTypes,
  dailyCapacityCount,
}: IRegisterAppointmentFormHeaderProps) {
  const onDateChange = (date: Date) => {
    setUserClosedModal(false);
    builder.setDateOfConsultation(date);
    builder.setConsultationOutcomes([]);
  };

  const onPracticeSelected = (practice: IGeneralPractice | null) => {
    builder.setOrganisation(practice);
    builder.setPcnName(
      practice ? practice.primaryCareNetwork.organisationDescription : ''
    );
    builder.setIcsCcgName(
      practice
        ? practice.clinicalCommissioningGroup.organisationDescription
        : ''
    );
  };

  const onLateCancellationClicked = () => {
    const wasChecked = builder.lateCancellation;
    builder.setLateCancellation((x) => !x);

    if (!wasChecked) {
      const patientAgeRangeUnknownId = 8;
      const modeOfConsultationUnknownId = 12;
      const referralSourceUnknownId = 10;
      const consultationTypeUnknownId = 6;

      builder.setPatientAgeRange({
        patientAgeRangeId: patientAgeRangeUnknownId,
        patientAgeRangeDescription: 'Unknown',
      });
      builder.setModeOfContact({
        modeOfContactId: modeOfConsultationUnknownId,
        modeOfContactDescription: 'Unknown',
      });
      builder.setReferralSource({
        referralSourceId: referralSourceUnknownId,
        referralSourceDescription: 'Unknown',
      });
      builder.setConsultationType({
        consultationTypeId: consultationTypeUnknownId,
        consultationTypeDescription: 'Unknown',
      });
      builder.setFollowUpMethod(null);
      builder.setFollowUpReason(null);
    } else {
      builder.setPatientAgeRange(null);
      builder.setModeOfContact(null);
      builder.setReferralSource(null);
      builder.setConsultationType(null);
    }
  };

  const capacityIsZero = useMemo(() => {
    return !dailyCapacityCount;
  }, [dailyCapacityCount]);

  return (
    <div className="flex flex-col gap-6 py-6">
      <div className="grid grid-cols-3 gap-4">
        <TextField
          text={builder.pcnName}
          placeholderText="PCN Name"
          disabled
          className="w-full"
        />
        <TextField
          text={builder.icsCcgName}
          placeholderText="ICB Name"
          disabled
          className="w-full"
        />
        <TextField text={builder.fcpName} disabled />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <DatePicker
          value={builder.dateOfConsultation}
          error={
            !dailyCapacityCount ? (
              <p className="text-xs">No capacity for selected date</p>
            ) : null
          }
          onChange={onDateChange}
          size="lg"
          inputFormat="DD/MM/YYYY"
          maxDate={new Date()}
          clearable={false}
        />
        <Dropdown
          items={favouritePractices}
          getItemId={(x) => x.organisationId.toString()}
          getItemValue={(x) => x.organisationDescription}
          setSelectedItem={onPracticeSelected}
          selectedItem={builder.organisation}
          label="Select GP Practice"
          searchable
          disabled={capacityIsZero}
        />
        <Dropdown
          items={lookupTypes.patientAgeRanges}
          getItemId={(x) => x.patientAgeRangeId.toString()}
          getItemValue={(x) => x.patientAgeRangeDescription}
          setSelectedItem={builder.setPatientAgeRange}
          selectedItem={builder.patientAgeRange}
          label="Patient Age Range"
          className="w-full"
          disabled={capacityIsZero}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Dropdown
          items={lookupTypes.modeOfContacts}
          getItemId={(x) => x.modeOfContactId.toString()}
          getItemValue={(x) => x.modeOfContactDescription}
          setSelectedItem={builder.setModeOfContact}
          selectedItem={builder.modeOfContact}
          label="Mode of consultation"
          className="w-full"
          disabled={capacityIsZero}
        />
        <Dropdown
          items={lookupTypes.referralSources}
          getItemId={(x) => x.referralSourceId.toString()}
          getItemValue={(x) => x.referralSourceDescription}
          setSelectedItem={builder.setReferralSource}
          selectedItem={builder.referralSource}
          label="Referral source"
          className="w-full"
          disabled={capacityIsZero}
        />
        <div className="space-y-2">
          <Dropdown
            items={lookupTypes.consultationTypes}
            getItemId={(x) => x.consultationTypeId.toString()}
            getItemValue={(x) => x.consultationTypeDescription}
            setSelectedItem={builder.setConsultationType}
            selectedItem={builder.consultationType}
            label="Consultation type"
            className="w-full"
            disabled={capacityIsZero}
          />
          {consultationTypeRequiresFollowUp(
            builder.consultationType?.consultationTypeId
          ) && (
            <div className="space-y-2">
              <Dropdown
                items={lookupTypes.followUpMethods}
                getItemId={(x) => x.followUpMethodId.toString()}
                getItemValue={(x) => x.followUpMethodDescription}
                setSelectedItem={builder.setFollowUpMethod}
                selectedItem={builder.followUpMethod}
                label="Follow up method"
                className="w-full"
                disabled={capacityIsZero}
              />
              <Dropdown
                items={lookupTypes.followUpReasons}
                getItemId={(x) => x.followUpReasonId.toString()}
                getItemValue={(x) => x.followUpReasonDescription}
                setSelectedItem={builder.setFollowUpReason}
                selectedItem={builder.followUpReason}
                label="Follow up reason"
                className="w-full"
                disabled={capacityIsZero}
              />
            </div>
          )}
        </div>
      </div>
      <Divider className="my-4" />
      <div className="grid grid-cols-4 gap-4">
        <Checkbox
          label="Did Not Attend"
          onChange={() => builder.setDidNotAttend((x) => !x)}
          checked={builder.didNotAttend}
          disabled={capacityIsZero}
          size="lg"
        />
        <Checkbox
          label="Late Cancellation"
          onChange={onLateCancellationClicked}
          checked={builder.lateCancellation}
          disabled={capacityIsZero}
          size="lg"
        />
        <Checkbox
          label="Interpreter Required"
          onChange={() => builder.setInterpreterRequired((x) => !x)}
          checked={builder.interpreterRequired}
          disabled={capacityIsZero}
          size="lg"
        />
        <div className="flex flex-col gap-2">
          <Checkbox
            label="Double Appointment"
            onChange={() => builder.setDoubleAppointment((x) => !x)}
            checked={builder.doubleAppointment}
            disabled={capacityIsZero}
            size="lg"
          />
          {builder.doubleAppointment && (
            <Dropdown
              items={lookupTypes.doubleAppointmentReasons}
              getItemId={(x) => x.doubleAppointmentReasonId.toString()}
              getItemValue={(x) => x.doubleAppointmentReasonDescription}
              setSelectedItem={builder.setDoubleAppointmentReason}
              selectedItem={builder.doubleAppointmentReason}
              label="Double Appointment Reason"
              className="w-full"
              disabled={capacityIsZero}
              size="sm"
            />
          )}
        </div>
      </div>
      <Divider className="mt-4" />
    </div>
  );
}

export default RegisterAppointmentFormHeader;
